import { bearbeiterKeys } from "../../../utils/vorgangScope";

/**
 * Reine Ableitungen der Vorgang-Detail-Personenzeile (SP6). In ein eigenes
 * Modul gezogen, damit die Logik ohne DOM getestet werden kann (vitest
 * `environment: node`).
 */

/** Die drei editierbaren Beteiligten-Gruppen des Beteiligten-Popups. */
export type BeteiligtenFeld = "hauptverantwortlich" | "bearbeiter" | "zuInformierende";

/** Anzeige-Label je Gruppe — „Bearbeiter" heißt in der UI „weitere Bearbeiter". */
export const BETEILIGTEN_LABELS: Record<BeteiligtenFeld, string> = {
    hauptverantwortlich: "Hauptverantwortliche",
    bearbeiter: "weitere Bearbeiter",
    zuInformierende: "zu informieren",
};

/** Stabile Reihenfolge der Gruppen im Popup. */
export const BETEILIGTEN_FELDER: BeteiligtenFeld[] = [
    "hauptverantwortlich",
    "bearbeiter",
    "zuInformierende",
];

/**
 * Beteiligten-Gruppen am To-do in der ALTANSICHT (`VorgangTodosCard`). Dort
 * zeigt die Aufgabe alle drei Rollen in EINEM Popup, damit ihre Zuständigkeit
 * ohne Wechsel in eine andere Ansicht vollständig bearbeitbar ist.
 *
 * Die chronologische Ansicht (v3) trennt „Beteiligte" und „Zu informieren" in
 * zwei Felder und nimmt deshalb die beiden Konstanten darunter.
 */
export const TODO_BETEILIGTEN_FELDER: BeteiligtenFeld[] = BETEILIGTEN_FELDER;

/**
 * „Beteiligte" OHNE die zu Informierenden.
 *
 * PO 04.09.2026: „aktuell werden beim neuen Design v3 ‚zu informieren‘ wie
 * Hauptverantwortliche angezeigt — zu informieren ist separat." In v3 standen
 * beide Gruppen nebeneinander UND die Info-Empfänger zusätzlich im
 * Beteiligten-Feld; dieselben Avatare erschienen doppelt.
 *
 * Als benannte Konstante statt als Inline-`filter` an mehreren Stellen: die
 * Regel steht einmal, und wer sie ändert, ändert sie überall.
 */
export const BETEILIGTEN_FELDER_OHNE_INFO: BeteiligtenFeld[] = BETEILIGTEN_FELDER.filter(
    (feld) => feld !== "zuInformierende",
);

/** Gegenstück: nur die zu Informierenden — das eigene Feld daneben. */
export const NUR_ZU_INFORMIEREN: BeteiligtenFeld[] = ["zuInformierende"];

/**
 * Ersteller-Id des Vorgangs: bevorzugt `createdByUserId`, sonst der
 * Record-Owner `ownerUserId` (Alt-Bestand ohne expliziten Ersteller).
 */
export function resolveErstellerId(v: {
    createdByUserId?: string | null;
    ownerUserId?: string | null;
}): string | null {
    return v.createdByUserId?.trim() || v.ownerUserId?.trim() || null;
}

/**
 * Deduplizierte Union aller Beteiligten (Hauptverantwortliche + weitere
 * Bearbeiter + zu Informierende) in stabiler Reihenfolge — für den
 * Avatar-Stack. Normalisiert Ids/Objekte via `bearbeiterKeys`.
 */
export function beteiligtePersonKeys(
    hauptverantwortlich: unknown[] | undefined,
    bearbeiter: unknown[] | undefined,
    zuInformierende: unknown[] | undefined,
): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const key of [
        ...bearbeiterKeys(hauptverantwortlich),
        ...bearbeiterKeys(bearbeiter),
        ...bearbeiterKeys(zuInformierende),
    ]) {
        if (!seen.has(key)) {
            seen.add(key);
            out.push(key);
        }
    }
    return out;
}

/**
 * Deduplizierte Union der Personen-Keys über die aktiven Beteiligten-Gruppen.
 * `felder` steuert, welche Gruppen einfließen (Default: alle) — die Todo-Page
 * blendet damit „Hauptverantwortliche" aus.
 */
export function beteiligtePersonKeysFor(
    values: Record<BeteiligtenFeld, string[]>,
    felder: BeteiligtenFeld[] = BETEILIGTEN_FELDER,
): string[] {
    const seen = new Set<string>();
    for (const feld of felder) {
        for (const key of values[feld] ?? []) {
            if (key) seen.add(key);
        }
    }
    return [...seen];
}

/**
 * Keys für den Beteiligten-Avatar-Stack der Übersicht: die aktiven Beteiligten-
 * Gruppen (dedupliziert) plus der Ersteller GANZ HINTEN — aber nur, wenn er nicht
 * ohnehin schon beteiligt ist. Ist der Ersteller zugleich Bearbeiter/HV/zu
 * informieren, erscheint er genau einmal an seiner Beteiligten-Position (nie
 * doppelt). Der Ersteller ist rein informativ und über das Popup nicht editierbar.
 */
export function beteiligteStackKeys(
    values: Record<BeteiligtenFeld, string[]>,
    erstellerId: string | null | undefined,
    felder: BeteiligtenFeld[] = BETEILIGTEN_FELDER,
): string[] {
    const keys = beteiligtePersonKeysFor(values, felder);
    const ers = erstellerId?.trim();
    if (ers && !keys.includes(ers)) keys.push(ers);
    return keys;
}
